import { useState } from 'react';
import { useLang } from '../../../hooks/useLang';

interface SubscribeModalProps {
  open: boolean;
  onClose: () => void;
}

export default function SubscribeModal({ open, onClose }: SubscribeModalProps) {
  const { t } = useLang();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [notifyNew, setNotifyNew] = useState(true);
  const [notifyWeekly, setNotifyWeekly] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const validateEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setError(t('请输入邮箱', 'Please enter your email')); return; }
    if (!validateEmail(email)) { setError(t('请输入正确的邮箱格式', 'Please enter a valid email')); return; }
    setError('');
    setSubmitted(true);
  };

  const handleClose = () => {
    onClose();
    if (submitted) {
      setTimeout(() => { setSubmitted(false); setEmail(''); setName(''); }, 300);
    }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center px-4" onClick={e => { if (e.target === e.currentTarget) handleClose(); }}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
      <div className="relative bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#2E1065] to-[#4C1D95] px-6 py-6 text-white relative overflow-hidden">
          <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-white/5"></div>
          <div className="absolute -bottom-4 -left-4 w-20 h-20 rounded-full bg-[#F59E0B]/10"></div>
          <button onClick={handleClose} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-white/60 hover:text-white cursor-pointer">
            <i className="ri-close-line text-xl"></i>
          </button>
          <div className="flex items-center gap-3 mb-2 relative">
            <div className="w-10 h-10 bg-[#F59E0B]/20 rounded-xl flex items-center justify-center">
              <i className="ri-mail-send-line text-[#F59E0B] text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-bold">{t('订阅清鉴纵论', 'Subscribe to ChaInsight Column')}</h3>
              <p className="text-white/60 text-xs">{t('第一时间获取清鉴元衡先生的最新深度文章', 'Be the first to receive new articles')}</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {!submitted ? (
            <>
              <div className="flex items-start gap-3 bg-[#7C3AED]/5 border border-[#7C3AED]/15 rounded-xl p-4 mb-6">
                <div className="w-8 h-8 flex items-center justify-center shrink-0">
                  <i className="ri-quill-pen-line text-[#7C3AED] text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-800 mb-1">{t('订阅后您将收到：', 'Subscribers receive:')}</p>
                  <ul className="text-xs text-gray-500 space-y-1">
                    <li className="flex items-center gap-1.5">
                      <i className="ri-checkbox-circle-line text-[#7C3AED] text-xs"></i>
                      {t('每篇新文章发布后第一时间邮件推送', 'Email notification for every new article')}
                    </li>
                    <li className="flex items-center gap-1.5">
                      <i className="ri-checkbox-circle-line text-[#7C3AED] text-xs"></i>
                      {t('每周RWA行业要闻摘要（可选）', 'Weekly RWA news digest (optional)')}
                    </li>
                    <li className="flex items-center gap-1.5">
                      <i className="ri-checkbox-circle-line text-[#7C3AED] text-xs"></i>
                      {t('订阅者专属深度资料包（不定期）', 'Exclusive in-depth materials (occasional)')}
                    </li>
                  </ul>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">{t('您的称谓（选填）', 'Your name (optional)')}</label>
                  <input
                    type="text"
                    placeholder={t('如：行业从业者、投资人...', 'e.g. Industry Professional')}
                    value={name}
                    onChange={e => setName(e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#7C3AED]"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">{t('邮箱地址', 'Email Address')} <span className="text-red-400">*</span></label>
                  <input
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={e => { setEmail(e.target.value); setError(''); }}
                    className={`w-full border rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#7C3AED] ${error ? 'border-red-300' : 'border-gray-200'}`}
                  />
                  {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
                </div>
                <div className="space-y-2">
                  <label
                    className="flex items-center gap-3 cursor-pointer"
                    onClick={() => setNotifyNew(!notifyNew)}
                  >
                    <div className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-all ${notifyNew ? 'bg-[#7C3AED] border-[#7C3AED]' : 'border-gray-300'}`}>
                      {notifyNew && <i className="ri-check-line text-white text-xs"></i>}
                    </div>
                    <span className="text-sm text-gray-700">{t('新文章发布通知', 'New article notifications')}</span>
                  </label>
                  <label
                    className="flex items-center gap-3 cursor-pointer"
                    onClick={() => setNotifyWeekly(!notifyWeekly)}
                  >
                    <div className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-all ${notifyWeekly ? 'bg-[#7C3AED] border-[#7C3AED]' : 'border-gray-300'}`}>
                      {notifyWeekly && <i className="ri-check-line text-white text-xs"></i>}
                    </div>
                    <span className="text-sm text-gray-700">{t('每周行业要闻摘要', 'Weekly industry digest')}</span>
                  </label>
                </div>
                <button
                  type="submit"
                  className="w-full py-3 bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] text-white font-bold rounded-xl hover:opacity-90 transition-opacity cursor-pointer mt-2"
                >
                  {t('立即订阅', 'Subscribe Now')}
                </button>
                <p className="text-xs text-gray-400 text-center">
                  {t('订阅后可随时退订，我们尊重您的隐私', 'Unsubscribe anytime. We respect your privacy.')}
                </p>
              </form>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-gradient-to-br from-[#7C3AED]/10 to-[#A78BFA]/10 rounded-full flex items-center justify-center mx-auto mb-5">
                <i className="ri-mail-check-line text-[#7C3AED] text-4xl"></i>
              </div>
              <h4 className="text-gray-900 font-bold text-lg mb-2">{t('订阅成功！', 'Subscription Successful!')}</h4>
              <p className="text-sm text-gray-500 mb-1">{name && `${name}，`}{t('欢迎加入清鉴纵论读者群', 'Welcome to the ChaInsight Column reader community')}</p>
              <p className="text-sm text-gray-400 mb-6">{t('确认邮件已发送至', 'Confirmation email sent to')} <span className="text-[#7C3AED] font-medium">{email}</span></p>
              <div className="bg-[#7C3AED]/5 rounded-xl p-4 text-left">
                <p className="text-xs font-medium text-gray-700 mb-2">{t('您已订阅：', 'You subscribed to:')}</p>
                {notifyNew && <p className="text-xs text-gray-500 flex items-center gap-1.5 mb-1"><i className="ri-check-line text-[#7C3AED]"></i>{t('新文章发布通知', 'New article notifications')}</p>}
                {notifyWeekly && <p className="text-xs text-gray-500 flex items-center gap-1.5"><i className="ri-check-line text-[#7C3AED]"></i>{t('每周行业要闻摘要', 'Weekly industry digest')}</p>}
              </div>
              <button onClick={handleClose} className="mt-5 px-8 py-2.5 bg-[#7C3AED] text-white text-sm font-medium rounded-lg hover:bg-[#6D28D9] transition-colors cursor-pointer">
                {t('继续阅读', 'Continue Reading')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}